'use client'

import { HTMLProps, PropsWithChildren, useState } from 'react'
import clsx from 'classnames'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import Highlighter from 'react-highlight-words'
import { motion } from 'framer-motion'
import React from 'react'
import { safePolygon, useDelayGroup, useFloating, useHover, useInteractions } from '@floating-ui/react'
import { Link } from '@/routing'
import { Text } from '@/shared/ui'
import { ImgPlaceholder } from '@/shared/icons/img-placeholder'
import { IBlockPersonsItem } from '@/interfaces/blocks'

import { useAppearance } from '@/shared/utils'
import { variantsHeadline } from '@/shared/constants/variants'
import style from './expert.module.scss'

interface MemberProps extends Omit<HTMLProps<HTMLDivElement>, 'name' | 'id' | 'role'>, IBlockPersonsItem {
  searchValue: string
}

const Container = ({
  id,
  postCount,
  className,
  children,
}: { id: string | number; postCount: number; className?: string } & PropsWithChildren) => {
  return postCount > 0 ? (
    <Link className={className} href={`/analytics?authorId=${id}`}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  )
}

export const Expert = (props: MemberProps) => {
  const { className, person, role, searchValue } = props
  const { image, name, position, id, postCount } = person
  const splitedName = name.split(' ')
  const translate = useTranslations()
  const { ref: innerRef, animate } = useAppearance()
  const [isOpen, setIsOpen] = useState(false)
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  })
  const { delay } = useDelayGroup(context)
  const hover = useHover(context, { delay, handleClose: safePolygon() })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <motion.div ref={innerRef} animate={animate} variants={variantsHeadline} className={style.wrapper}>
      <Container id={id} postCount={postCount} className={clsx(style.container, className, { [style.isOpen]: isOpen })}>
        {image ? (
          <div className={style.avatar}>
            <Image src={image.url} alt={name} style={{ objectFit: 'cover' }} sizes="fill" fill />
          </div>
        ) : (
          <ImgPlaceholder className={style.avatar} />
        )}

        <Text className={style.name} color="dark" type="h3" tagName="span">
          <Highlighter
            highlightClassName={clsx('text', 'text_type_h3', 'text_color_main-blue')}
            searchWords={[searchValue]}
            autoEscape={true}
            textToHighlight={splitedName[0]}
            highlightTag={'span'}
          />
          <br />
          <Highlighter
            highlightClassName={clsx('text', 'text_type_h3', 'text_color_main-blue')}
            searchWords={[searchValue]}
            autoEscape={true}
            textToHighlight={splitedName[1]}
            highlightTag={'span'}
          />
        </Text>

        <Text
          className={style.position}
          color="dark-040"
          type="d"
          tagName="p"
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          {role || position}
        </Text>
        <Text type="d" className={clsx(style.publications, { [style.isEmpty]: !postCount })}>
          {postCount ? translate('linkPublications') + ' • ' + postCount : translate('linkEmptyPublications')}
        </Text>
      </Container>
      {isOpen && (
        <div className={style.tooltipContainer} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <p className={clsx(style.tooltip, 'text text_type_d text_color_dark-040', { [style.isOpen]: isOpen })}>
            {role || position}
          </p>
        </div>
      )}
    </motion.div>
  )
}
